import classNames from 'clsx';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { matchPath, useLocation } from 'react-router';
import { NavLink } from 'react-router-dom';
import scrollIntoView from 'scroll-into-view-if-needed';

import StarsBadge from '@swf/components/StarsBadge/StarsBadge';
import Translation from '@swf/components/Translation/Translation';
import ArrowIcon from '@swf/core/assets/icons/arrow.svg';
import type { ResolverTag } from '@swf/core/enums';
import usePrevious from '@swf/core/hooks/usePrevious';
import useResolver from '@swf/core/hooks/useResolver';
import type { TranslationTag } from '@swf/core/types/i18n';

import StarsNavAdditionalIcon from '../StarsNavAdditionalIcon/StarsNavAdditionalIcon';
import StarsNavList from '../StarsNavList/StarsNavList';

import useSectionActive from './useSectionActive';

export interface NavStyleProps {
  classes: {
    active: string;
    navListItem: string;
    navListItemActive: string;
    navItem: string;
    openLink: string;
    icon: string;
    arrow: string;
    navList?: string;
    open?: string;
  };
}
export interface Props {
  icon?: React.FC<React.SVGAttributes<SVGElement>>;
  tag?: TranslationTag;
  path?: string;
  onClick?(): void;
  badge?: React.ComponentType;
  subNav?: Props[];
  visible?: boolean;
  resolverTag?: ResolverTag;
  target?: React.AnchorHTMLAttributes<HTMLAnchorElement>['target'];
  indent?: number;
}

const StarsNavItem: React.FC<Props & NavStyleProps> = ({
  icon,
  tag,
  path,
  onClick,
  badge,
  classes,
  subNav,
  resolverTag,
  target,
  indent
}) => {
  const url = useResolver(resolverTag);

  const location = useLocation();
  const { pathname } = location;
  const previousLocation = usePrevious(location);
  const scrollRef = useRef<HTMLAnchorElement>(null);

  const shouldOpen = useCallback(
    (): boolean => !!(subNav && path && matchPath(pathname, { path })),
    [pathname, path, subNav]
  );

  const isSectionActive = useSectionActive({ indent, path, subNav, pathname });

  const [isOpen, setIsOpen] = useState(shouldOpen());

  const handleClick = () => setIsOpen(!isOpen);

  useEffect(() => {
    const open = shouldOpen();
    if (previousLocation !== location && !isOpen && open) {
      setIsOpen(open);
    }
    if (pathname === path && scrollRef.current) {
      scrollIntoView(scrollRef.current, {
        scrollMode: 'if-needed',
        block: 'center',
        inline: 'center'
      });
    }
  }, [location, previousLocation, isOpen, setIsOpen, shouldOpen, pathname, path]);

  const renderBody = () => {
    const className = classNames(classes.navItem, { [classes.openLink]: isOpen });

    const children = (
      <>
        {icon && React.createElement(icon, { className: classes.icon })}
        <Translation tagName="span" {...(tag as TranslationTag)} />
        <StarsNavAdditionalIcon path={path} isOpen={isOpen} />
        {subNav && <ArrowIcon className={classes.arrow} />}
        {badge && <StarsBadge component={badge} />}
      </>
    );

    if (onClick) {
      return (
        <button className={className} onClick={onClick}>
          {children}
        </button>
      );
    }

    if (resolverTag) {
      return (
        <a className={className} href={url} target={target || '_self'} rel="noopener noreferrer">
          {children}
        </a>
      );
    }

    if (subNav) {
      return (
        <span className={className} onClick={handleClick}>
          {children}
        </span>
      );
    }

    return (
      <NavLink
        to={path as string}
        activeClassName={classes.active}
        className={className}
        ref={scrollRef}
      >
        {children}
      </NavLink>
    );
  };

  return (
    <li
      className={classNames(classes.navListItem, {
        [classes.navListItemActive]: isSectionActive
      })}
    >
      {renderBody()}
      {subNav && (
        <StarsNavList
          list={subNav}
          isOpen={isOpen}
          classes={classes}
          indent={typeof indent !== 'undefined' ? indent + 1 : 1}
        />
      )}
    </li>
  );
};

export default StarsNavItem;
